import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { usePageTitle } from '../hooks/usePageTitle'
import { useLang } from '../context/LangContext'
import bcrypt from 'bcryptjs'

// Импорты изображений
import logoQaraa from '../images/logo-qaraa.webp'

/* ── Eye Icon SVG (как в AuthScreens) ── */
function EyeIcon({ visible }) {
  const pupilLeftX = visible ? 16 : 11;
  const pupilRightX = visible ? 38 : 33;
  return (
    <svg width="24" height="14" viewBox="0 0 50 28" fill="none" style={{ display: "block" }}>
      <ellipse cx="14" cy="14" rx="12" ry="12" fill="#FBFBFB" stroke="#E4E2DB" strokeWidth="0.5" />
      <circle cx={pupilLeftX} cy="14" r="4" fill="#414141" />
      <ellipse cx="36" cy="14" rx="12" ry="12" fill="#FBFBFB" stroke="#E4E2DB" strokeWidth="0.5" />
      <circle cx={pupilRightX} cy="14" r="4" fill="#414141" />
    </svg>
  );
}

export default function Profile({ customer, onLogout, hideHeader = false }) {
  const { t, setLang, lang } = useLang()
  if (!hideHeader) usePageTitle(t('profile.title') || 'Профиль')
  const navigate = useNavigate()
  
  // Translations for modals
  const modalTranslations = {
    edit: {
      title: lang === 'kk' ? 'Профильді өзгерту' : lang === 'en' ? 'Edit Profile' : 'Редактировать профиль',
      subtitle: lang === 'kk' ? 'Ақпаратыңызды жаңартыңыз' : lang === 'en' ? 'Update your information' : 'Обновите вашу информацию',
      uploadAvatar: lang === 'kk' ? 'Аватар жүктеу' : lang === 'en' ? 'Upload Avatar' : 'Загрузить аватар',
      avatarHint: lang === 'kk' ? 'JPG, PNG немесе GIF' : lang === 'en' ? 'JPG, PNG or GIF' : 'JPG, PNG или GIF',
      reset: lang === 'kk' ? 'Қалпына келтіру' : lang === 'en' ? 'Reset' : 'Сброс',
      cancel: lang === 'kk' ? 'Бас тарту' : lang === 'en' ? 'Cancel' : 'Отмена',
      save: lang === 'kk' ? 'Сақтау' : lang === 'en' ? 'Save' : 'Сохранить',
      uploading: lang === 'kk' ? 'Жүктелуде...' : lang === 'en' ? 'Uploading...' : 'Загрузка...'
    },
    security: {
      title: lang === 'kk' ? 'Қауіпсіздік орталығы' : lang === 'en' ? 'Security Center' : 'Центр безопасности',
      subtitle: lang === 'kk' ? 'Қауіпсіздік үшін құпия сөзіңізді жаңартыңыз' : lang === 'en' ? 'Update your password for security' : 'Обновите ваш пароль для безопасности',
      currentPassword: lang === 'kk' ? 'Қазіргі құпия сөз' : lang === 'en' ? 'Current Password' : 'Текущий пароль',
      newPassword: lang === 'kk' ? 'Жаңа құпия сөз' : lang === 'en' ? 'New Password' : 'Новый пароль',
      confirmPassword: lang === 'kk' ? 'Құпия сөзді растаңыз' : lang === 'en' ? 'Confirm Password' : 'Подтвердите пароль',
      updateBtn: lang === 'kk' ? 'Жаңарту' : lang === 'en' ? 'Update' : 'Обновить',
      updating: lang === 'kk' ? 'Жаңартылуда...' : lang === 'en' ? 'Updating...' : 'Обновление...',
      continue: lang === 'kk' ? 'Жалғастыру' : lang === 'en' ? 'Continue' : 'Продолжить'
    },
    language: {
      title: lang === 'kk' ? 'Тілді таңдаңыз' : lang === 'en' ? 'Select Language' : 'Выберите язык',
      close: lang === 'kk' ? 'Жабу' : lang === 'en' ? 'Close' : 'Закрыть'
    },
    support: {
      title: lang === 'kk' ? 'Қолдау' : lang === 'en' ? 'Support' : 'Поддержка',
      description: lang === 'kk' ? 'Кеңес немесе көмек алу үшін Telegram арқылы жазыңыз' : lang === 'en' ? 'Contact us on Telegram for help' : 'Свяжитесь с нами в Telegram для получения помощи',
      close: lang === 'kk' ? 'Жабу' : lang === 'en' ? 'Close' : 'Закрыть'
    },
    logout: {
      title: lang === 'kk' ? 'Аккаунттан шығу' : lang === 'en' ? 'Log Out?' : 'Выйти из аккаунта?',
      message: lang === 'kk' ? 'Сіз расыменде шыққыңыз келе ме? Шыққан жағдайда, аккаунтқа қайта кіру керек болады.' : lang === 'en' ? 'Are you sure you want to log out? You will need to log in again.' : 'Вы уверены, что хотите выйти? Вам придется снова войти в систему.',
      cancel: lang === 'kk' ? 'Бас тарту' : lang === 'en' ? 'Cancel' : 'Отмена',
      logout: lang === 'kk' ? 'Шығу' : lang === 'en' ? 'Log Out' : 'Выйти'
    }
  }
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [showSupportModal, setShowSupportModal] = useState(false)
  const [showLangSelector, setShowLangSelector] = useState(false)
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [profile, setProfile] = useState(customer || {})
  const [loadingProfile, setLoadingProfile] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [firstNameInput, setFirstNameInput] = useState('')
  const [lastNameInput, setLastNameInput] = useState('')
  const [avatarData, setAvatarData] = useState(null) // preview DataURL до сохранения
  const [avatarFile, setAvatarFile] = useState(null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [showSecurityModal, setShowSecurityModal] = useState(false)
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [updatingPassword, setUpdatingPassword] = useState(false)
  const [showOldPass, setShowOldPass] = useState(false)
  const [showNewPass, setShowNewPass] = useState(false)
  const [showConfirmPass, setShowConfirmPass] = useState(false)
  const [cardFlipped, setCardFlipped] = useState(false)
  const [passwordFormValid, setPasswordFormValid] = useState(false)

  // Clear form when modal closes
  useEffect(() => {
    if (!showSecurityModal) {
      setOldPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPasswordError('')
      setPasswordSuccess('')
      setShowOldPass(false)
      setShowNewPass(false)
      setShowConfirmPass(false)
      setPasswordFormValid(false)
    }
  }, [showSecurityModal])

  // Validate password form
  useEffect(() => {
    setPasswordFormValid(
      oldPassword.trim().length > 0 &&
      newPassword.trim().length > 0 &&
      confirmPassword.trim().length > 0
    )
  }, [oldPassword, newPassword, confirmPassword])

  const formatPhone = (phone) => {
    if (!phone) return ''
    // Очищаем от всех нецифр
    const d = String(phone).replace(/\D/g, '')
    if (d.length !== 11) return phone // Если не 11 цифр, возвращаем как есть

    // Формат: 8-776-888-30-07
    const operator = d.slice(1, 4)
    const part1 = d.slice(4, 7)
    const part2 = d.slice(7, 9)
    const part3 = d.slice(9, 11)

    return `8-${operator}-${part1}-${part2}-${part3}`
  }

  const formatDate = (dateString, fieldName = 'unknown') => {
    if (!dateString) return 'Не указана'

    let date = null

    // Проверяем, если это формат DD.MM.YYYY или DD.MM.YY
    if (typeof dateString === 'string' && dateString.includes('.')) {
      const parts = dateString.split('.')
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10)
        const month = parseInt(parts[1], 10) - 1 // месяцы в JS начинаются с 0
        let year = parseInt(parts[2], 10)

        // Если год двузначный, добавляем 2000
        if (year < 100) {
          year += 2000
        }

        date = new Date(year, month, day)
      }
    }

    // Если не получилось с точками, пробуем стандартный парсинг
    if (!date || isNaN(date.getTime())) {
      date = new Date(dateString)

      // Если все еще невалидная, пробуем ISO формат
      if (isNaN(date.getTime()) && typeof dateString === 'string') {
        date = new Date(dateString + 'T00:00:00.000Z')
      }
    }

    if (!date || isNaN(date.getTime())) {
      return 'Не указана'
    }

    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  // Отладочная информация для профиля (можно удалить после тестирования)
  useEffect(() => {
    if (profile.birth_date) {
      console.log('Birth date:', profile.birth_date, 'formatted:', formatDate(profile.birth_date, 'birth_date'))
    }
  }, [profile.birth_date])

  const formatCardNumber = (num) => {
    if (!num) return 'Не назначен'
    const digits = String(num).replace(/\D/g, '')
    return digits.replace(/(.{4})/g, '$1 ').trim()
  }

  const maskedCardNumber = (num) => {
    if (!num) return 'Не назначен'
    const d = String(num).replace(/\D/g, '')
    if (d.length < 8) return formatCardNumber(d)
    const first = d.slice(0, 4)
    const last = d.slice(-4)
    return `${first} •••• •••• ${last}`
  }

  useEffect(() => {
    const fetchProfile = async () => {
      if (!customer) return
      try {
        setLoadingProfile(true)
        let fetched = null

        if (customer.id) {
          const { data } = await supabase
            .from('customers')
            .select('id, fullname, phone, card_number, bonus_balance, total_spent, created_at, registration_date, last_purchase_date, birth_date')
            .eq('id', customer.id)
            .single()
          if (data) fetched = data
        }

        if (!fetched && customer.phone) {
          const raw = String(customer.phone)
          const onlyDigits = raw.replace(/\D/g, '')
          const withPlus = raw.startsWith('+') ? raw : '+' + onlyDigits
          const variants = [raw, onlyDigits, withPlus]

          for (const ph of variants) {
            const { data } = await supabase
              .from('customers')
              .select('id, fullname, phone, card_number, bonus_balance, total_spent, created_at, registration_date, last_purchase_date, birth_date')
              .eq('phone', ph)
              .maybeSingle()
            if (data) { fetched = data; break }
          }
        }

        if (fetched) {
          setProfile({ ...fetched })
          await recomputeFromLedger(fetched.id)
          setTimeout(() => { recomputeFromLedger(fetched.id) }, 350)
        }
      } catch (_) {
        // ignore
      } finally {
        setLoadingProfile(false)
      }
    }
    fetchProfile()
  }, [customer?.id, customer?.phone])

  // Быстрая подстраховка: короткий пуллинг после захода на профиль, чтобы поймать свежие изменения
  useEffect(() => {
    if (!profile?.id) return
    let ticks = 0
    const maxTicks = 10 // ~5 секунд при 500мс
    const timer = setInterval(async () => {
      ticks++
      // подтянуть строку клиента
      try {
        const { data } = await supabase
          .from('customers')
          .select('id, fullname, phone, card_number, bonus_balance, total_spent, created_at, registration_date, last_purchase_date, birth_date')
          .eq('id', profile.id)
          .maybeSingle()
        if (data) setProfile(prev => ({ ...prev, ...data }))
      } catch (_) { }
      await recomputeFromLedger(profile.id)
      if (ticks >= maxTicks) clearInterval(timer)
    }, 500)
    return () => clearInterval(timer)
  }, [profile?.id])

  // Общий пересчет из ledger
  const recomputeFromLedger = async (cid) => {
    try {
      const { data: rows } = await supabase
        .from('bonuses_ledger')
        .select('type, amount, status, created_at')
        .eq('customer_id', cid)

      if (Array.isArray(rows)) {
        let total = 0
        let lastDate = null
        for (const r of rows) {
          if (r.status === 'active') {
            if (r.type === 'credit') total += (r.amount || 0)
            else if (r.type === 'debit' || r.type === 'reversal') total -= (r.amount || 0)
          }
          const dt = r.created_at ? new Date(r.created_at) : null
          if (dt && (!lastDate || dt > lastDate)) lastDate = dt
        }
        const patch = { bonus_balance: total }
        if (lastDate) patch.last_purchase_date = lastDate.toISOString()
        setProfile(prev => ({ ...prev, ...patch }))
      }
    } catch (_) { }
  }

  // Принудительное обновление при фокусе окна / возвращении вкладки / изменении локального кеша
  useEffect(() => {
    const onFocus = () => {
      if (profile?.id) {
        // Подтянуть свежие данные профиля и пересчитать из ledger
        supabase.from('customers').select('id, fullname, phone, card_number, bonus_balance, total_spent, created_at, registration_date, last_purchase_date, birth_date').eq('id', profile.id).maybeSingle().then(({ data }) => {
          if (data) setProfile(prev => ({ ...prev, ...data }))
        })
        recomputeFromLedger(profile.id)
      }
    }
    const onVisibility = () => { if (document.visibilityState === 'visible') onFocus() }
    const onStorage = (e) => { if (e.key === 'qaraa_customer') onFocus() }

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('storage', onStorage)
    }
  }, [profile?.id])

  // Live updates for bonuses and profile changes
  useEffect(() => {
    if (!profile?.id) return
    const ch = supabase
      .channel('realtime-bonuses-' + profile.id)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'bonuses_ledger', filter: 'customer_id=eq.' + profile.id }, async () => {
        try {
          // При любой новой записи пересчитываем и бонусы, и последнюю покупку
          const { data: rows } = await supabase
            .from('bonuses_ledger')
            .select('type, amount, status, created_at')
            .eq('customer_id', profile.id)
          if (Array.isArray(rows)) {
            let total = 0
            let lastDate = null
            for (const r of rows) {
              if (r.status === 'active') {
                if (r.type === 'credit') total += (r.amount || 0)
                else if (r.type === 'debit' || r.type === 'reversal') total -= (r.amount || 0)
              }
              const dt = r.created_at ? new Date(r.created_at) : null
              if (dt && (!lastDate || dt > lastDate)) lastDate = dt
            }
            const patch = { bonus_balance: total }
            if (lastDate) patch.last_purchase_date = lastDate.toISOString()
            setProfile(prev => ({ ...prev, ...patch }))
          }
        } catch (_) { }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'customers', filter: 'id=eq.' + profile.id }, async (payload) => {
        try {
          const { bonus_balance, last_purchase_date } = payload.new || {}
          const patch = {}
          if (typeof bonus_balance === 'number') patch.bonus_balance = bonus_balance
          if (last_purchase_date) patch.last_purchase_date = last_purchase_date
          if (Object.keys(patch).length) setProfile(prev => ({ ...prev, ...patch }))
        } catch (_) { }
      })
      .subscribe()

    return () => {
      try { supabase.removeChannel(ch) } catch (_) { }
    }
  }, [profile?.id])

  const handleLogout = () => {
    // Просто вызываем проп, редирект сделает App через роутинг (так как customer станет null)
    if (onLogout) onLogout()
  }

  useEffect(() => {
    setAvatarData(null)
    setAvatarFile(null)
  }, [profile.id])


  // Скролл к началу при загрузке
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  // Определение мобильной версии
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Закрытие меню при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuOpen && !event.target.closest('.mobile-profile-btn') && !event.target.closest('.mobile-profile-menu')) {
        setProfileMenuOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [profileMenuOpen])

  const capitalize = (str) => {
    if (!str) return ''
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  // Effect to sync inputs when editing starts
  useEffect(() => {
    if (showEdit && profile) {
      setFirstNameInput(profile.first_name || '')
      setLastNameInput(profile.last_name || '')
    }
  }, [showEdit, profile])

  const onPickAvatar = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = reader.result
      setAvatarData(dataUrl)
      setAvatarFile(file)
    }
    reader.readAsDataURL(file)
  }

  const saveProfileChanges = async () => {
    try {
      let newAvatarUrl = null
      if (avatarFile && profile.id) {
        setAvatarUploading(true)
        const ext = (avatarFile.name.split('.').pop() || 'jpg').toLowerCase()
        const path = `customers/${profile.id}/avatar_${Date.now()}.${ext}`
        const { error: upErr } = await supabase.storage.from('avatars').upload(path, avatarFile, {
          upsert: true,
          contentType: avatarFile.type || 'image/jpeg'
        })
        if (upErr) throw upErr
        const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path)
        newAvatarUrl = pub?.publicUrl || null
      }

      if (profile.id) {
        const patch = {}
        if (firstNameInput !== profile.first_name) patch.first_name = firstNameInput
        if (lastNameInput !== profile.last_name) patch.last_name = lastNameInput

        // Update fullname as well for backward compatibility if needed, though best to rely on first/last
        if (patch.first_name || patch.last_name) {
          patch.fullname = `${firstNameInput} ${lastNameInput}`.trim()
        }

        if (newAvatarUrl) patch.avatar_url = newAvatarUrl
        if (Object.keys(patch).length) {
          const { error } = await supabase.from('customers').update(patch).eq('id', profile.id)
          if (error) throw error
        }

        const updatedProfile = { ...profile, ...patch, ...(newAvatarUrl ? { avatar_url: newAvatarUrl } : {}) }
        setProfile(updatedProfile)
        localStorage.setItem('qaraa_customer', JSON.stringify(updatedProfile))
        setShowEdit(false)
      }
    } catch (_) {
      setShowEdit(false)
    } finally {
      setAvatarUploading(false)
    }
  }

  const handlePasswordUpdate = async (e) => {
    if (e) e.preventDefault()
    setPasswordError('')
    setPasswordSuccess('')

    if (newPassword !== confirmPassword) {
      setPasswordError(t('security.passwords_mismatch'))
      return
    }

    if (newPassword.length < 6) {
      setPasswordError(t('security.password_too_short'))
      return
    }

    try {
      setUpdatingPassword(true)

      // 1. Verify old password using bcrypt
      const isPasswordCorrect = await bcrypt.compare(oldPassword, profile.password)

      if (!isPasswordCorrect) {
        setPasswordError(t('security.incorrect_password'))
        return
      }

      // 2. Hash new password
      const hashedNewPassword = await bcrypt.hash(newPassword, 10)

      // 3. Update in customers table
      const { error } = await supabase
        .from('customers')
        .update({ password: hashedNewPassword })
        .eq('id', profile.id)

      if (error) throw error

      // Update local state and localStorage
      const updatedProfile = { ...profile, password: hashedNewPassword }
      setProfile(updatedProfile)
      localStorage.setItem('qaraa_customer', JSON.stringify(updatedProfile))

      setPasswordSuccess(t('security.success') || 'Пароль успешно изменен')
      setTimeout(() => {
        setShowSecurityModal(false)
      }, 2000)
    } catch (err) {
      setPasswordError(err.message)
    } finally {
      setUpdatingPassword(false)
    }
  }

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        
        /* AuthScreens-style cards */
        .profile-card-auth {
          background: #FFFFFF;
          border: 0.5px solid #EAEAE4;
          border-radius: 28px;
          transition: all 0.2s ease;
        }
        
        .profile-btn-auth {
          background: #F7F6F2;
          border: 0.5px solid #EAEAE4;
          border-radius: 13px;
          transition: all 0.15s ease;
          cursor: pointer;
        }
        
        .profile-btn-auth:hover {
          background: #FFFFFF;
          border-color: #C4B8F8;
        }
        
        .profile-input-auth {
          background: #F7F6F2;
          border: 0.5px solid #EAEAE4;
          border-radius: 13px;
          padding: 0 14px;
          height: 48px;
          font-size: 14.5px;
          color: #111;
          outline: none;
          transition: all 0.15s ease;
          font-family: inherit;
        }
        
        .profile-input-auth:focus {
          background: #FFFFFF;
          border-color: #C4B8F8;
        }
        
        .profile-btn-primary {
          width: 100%;
          height: 50px;
          background: #FFFFFF;
          color: #111;
          border: 1.5px solid #111;
          border-radius: 100px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          letter-spacing: -0.2px;
          font-family: inherit;
          transition: all 0.2s ease;
        }
        
        .profile-btn-primary:hover {
          transform: scale(1.01);
        }
        
        .profile-btn-primary:disabled {
          background: #F5F5F5;
          color: #C7C7CC;
          border-color: #E5E5EA;
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        /* Mobile Styles */
        @media (max-width: 768px) {
          .profile-stats { grid-template-columns: 1fr !important; }
          .profile-actions { 
            grid-template-columns: 1fr !important; 
            gap: 12px !important;
          }
        }
        
        /* Touch-friendly interactions */
        @media (hover: none) and (pointer: coarse) {
          .profile-card-auth:hover { transform: none !important; }
          .profile-card-auth:active { 
            transform: scale(0.98) !important;
            transition: transform 0.1s ease !important;
          }
          .profile-btn-auth:hover { transform: none !important; }
          .profile-btn-auth:active {
            transform: scale(0.96) !important;
            transition: transform 0.1s ease !important;
          }
        }
      `}</style>

      <div style={{
        minHeight: '100vh',
        background: '#FAFAF8',
        paddingBottom: 'env(safe-area-inset-bottom, 40px)'
      }}>
        {/* Header */}
        {!hideHeader && (
          <header style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            background: 'rgba(250, 250, 248, 0.8)',
            backdropFilter: 'saturate(180%) blur(20px)',
            WebkitBackdropFilter: 'saturate(180%) blur(20px)',
            borderBottom: '0.5px solid #EAEAE4'
          }}>
            <div className="cart-header-container" style={{ maxWidth: '1440px', margin: '0 auto', padding: '20px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div onClick={() => navigate('/dashboard')} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                cursor: 'pointer',
                transition: 'opacity 0.3s ease',
                WebkitTapHighlightColor: 'transparent'
              }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                onTouchStart={(e) => e.currentTarget.style.opacity = '0.7'}
                onTouchEnd={(e) => e.currentTarget.style.opacity = '1'}>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', position: 'relative' }}>
              </div>
            </div>
          </header>
        )}

        {/* Edit modal - AuthScreens Style */}
        {showEdit && (
          <div
            onClick={() => setShowEdit(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', animation: 'fadeIn 0.2s ease' }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: '420px', background: '#FFFFFF', borderRadius: '28px', padding: '28px 24px', border: '0.5px solid #EAEAE4', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
              <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '6px', color: '#111', letterSpacing: '-0.5px' }}>{modalTranslations.edit.title}</div>
                <p style={{ fontSize: '13px', color: '#9A9890', margin: 0, lineHeight: 1.5 }}>{modalTranslations.edit.subtitle}</p>
              </div>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '20px', padding: '16px', background: '#F7F6F2', borderRadius: '13px', border: '0.5px solid #EAEAE4' }}>
                {avatarData || profile.avatar_url ? (
                  <img src={avatarData || profile.avatar_url} alt="avatar" style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} />
                ) : (
                  <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(145deg, #F7F6F2, #EEECEA)', border: '0.5px solid #E4E2DB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9A9890" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', cursor: 'pointer', color: '#111', display: 'block', marginBottom: '4px' }}>
                    {modalTranslations.edit.uploadAvatar}
                    <input type="file" accept="image/*" onChange={onPickAvatar} style={{ display: 'none' }} />
                  </label>
                  <p style={{ fontSize: '12px', color: '#9A9890', margin: 0 }}>{modalTranslations.edit.avatarHint}</p>
                </div>
                {avatarData && (
                  <button onClick={() => { setAvatarData(null); setAvatarFile(null) }} style={{ border: '0.5px solid #EAEAE4', background: '#FFFFFF', borderRadius: '10px', padding: '8px 12px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', color: '#9A9890', fontFamily: 'inherit' }}>{modalTranslations.edit.reset}</button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#111', paddingLeft: '4px' }}>{t('profile.first_name')}</label>
                  <input
                    value={firstNameInput}
                    onChange={(e) => setFirstNameInput(capitalize(e.target.value))}
                    placeholder={t('profile.first_name')}
                    className="profile-input-auth"
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#111', paddingLeft: '4px' }}>{t('profile.last_name')}</label>
                  <input
                    value={lastNameInput}
                    onChange={(e) => setLastNameInput(capitalize(e.target.value))}
                    placeholder={t('profile.last_name')}
                    className="profile-input-auth"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              {/* Read-only fields */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#111', paddingLeft: '4px' }}>{t('profile.reg_date') || 'Дата регистрации'}</label>
                  <div style={{
                    width: '100%', padding: '0 14px', borderRadius: '13px', border: '0.5px solid #EAEAE4',
                    fontSize: '14.5px', background: '#F7F6F2', color: '#9A9890', height: '48px',
                    display: 'flex', alignItems: 'center'
                  }}>
                    {formatDate(profile.registration_date || profile.created_at)}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#111', paddingLeft: '4px' }}>{t('profile.birth_date') || 'Дата рождения'}</label>
                  <div style={{
                    width: '100%', padding: '0 14px', borderRadius: '13px', border: '0.5px solid #EAEAE4',
                    fontSize: '14.5px', background: '#F7F6F2', color: '#9A9890', height: '48px',
                    display: 'flex', alignItems: 'center'
                  }}>
                    {formatDate(profile.birth_date)}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                <button 
                  onClick={() => setShowEdit(false)} 
                  className="profile-btn-primary"
                  style={{
                    border: '1.5px solid #111',
                    background: '#FFFFFF',
                    color: '#111'
                  }}>
                  {modalTranslations.edit.cancel}
                </button>
                <button 
                  onClick={saveProfileChanges} 
                  disabled={avatarUploading} 
                  className="profile-btn-primary"
                  style={{
                    border: avatarUploading ? '1.5px solid #E5E5EA' : '1.5px solid #111',
                    background: avatarUploading ? '#F5F5F5' : '#FFFFFF',
                    color: avatarUploading ? '#C7C7CC' : '#111',
                    cursor: avatarUploading ? 'not-allowed' : 'pointer',
                    opacity: avatarUploading ? 0.6 : 1
                  }}>
                  {avatarUploading ? modalTranslations.edit.uploading : modalTranslations.edit.save}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Security modal - AuthScreens Style */}
        {showSecurityModal && (
          <div
            onClick={() => setShowSecurityModal(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', animation: 'fadeIn 0.2s ease' }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{ 
              width: '100%', 
              maxWidth: '420px', 
              background: '#FFFFFF', 
              borderRadius: '28px', 
              padding: '28px 24px',
              border: '0.5px solid #EAEAE4',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)', 
              animation: 'scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)' 
            }}>
              <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 6px 0', color: '#111', letterSpacing: '-0.5px' }}>{modalTranslations.security.title}</h2>
                <p style={{ fontSize: '13px', color: '#9A9890', margin: 0, lineHeight: 1.5 }}>{modalTranslations.security.subtitle}</p>
              </div>

              <form onSubmit={handlePasswordUpdate} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Old Password */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#111', paddingLeft: '4px' }}>{modalTranslations.security.currentPassword}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showOldPass ? "text" : "password"}
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="profile-input-auth"
                      style={{ width: '100%', paddingRight: '48px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowOldPass(!showOldPass)}
                      style={{ 
                        position: 'absolute', 
                        right: '12px', 
                        top: '50%', 
                        transform: 'translateY(-50%)', 
                        background: 'none', 
                        border: 'none', 
                        padding: '4px',
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center',
                        opacity: 0.6
                      }}
                    >
                      <EyeIcon visible={showOldPass} />
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#111', paddingLeft: '4px' }}>{modalTranslations.security.newPassword}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showNewPass ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="profile-input-auth"
                      style={{ width: '100%', paddingRight: '48px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPass(!showNewPass)}
                      style={{ 
                        position: 'absolute', 
                        right: '12px', 
                        top: '50%', 
                        transform: 'translateY(-50%)', 
                        background: 'none', 
                        border: 'none', 
                        padding: '4px',
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center',
                        opacity: 0.6
                      }}
                    >
                      <EyeIcon visible={showNewPass} />
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#111', paddingLeft: '4px' }}>{modalTranslations.security.confirmPassword}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showConfirmPass ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="profile-input-auth"
                      style={{ width: '100%', paddingRight: '48px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPass(!showConfirmPass)}
                      style={{ 
                        position: 'absolute', 
                        right: '12px', 
                        top: '50%', 
                        transform: 'translateY(-50%)', 
                        background: 'none', 
                        border: 'none', 
                        padding: '4px',
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center',
                        opacity: 0.6
                      }}
                    >
                      <EyeIcon visible={showConfirmPass} />
                    </button>
                  </div>
                </div>

                {passwordError && (
                  <div style={{ padding: '12px 16px', background: '#FFF5F5', borderRadius: '13px', color: '#DC2626', fontSize: '13px', fontWeight: '500', border: '0.5px solid rgba(220,38,38,0.1)' }}>
                    {passwordError}
                  </div>
                )}

                {passwordSuccess && (
                  <div style={{ padding: '12px 16px', background: '#F0FDF4', borderRadius: '13px', color: '#16A34A', fontSize: '13px', fontWeight: '500', border: '0.5px solid rgba(22,163,74,0.1)' }}>
                    {passwordSuccess}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!passwordFormValid || updatingPassword}
                  className="profile-btn-primary"
                  style={{ 
                    marginTop: '8px',
                    border: (!passwordFormValid || updatingPassword) ? '1.5px solid #E5E5EA' : '1.5px solid #111',
                    background: (!passwordFormValid || updatingPassword) ? '#F5F5F5' : '#FFFFFF',
                    color: (!passwordFormValid || updatingPassword) ? '#C7C7CC' : '#111',
                    cursor: (!passwordFormValid || updatingPassword) ? 'not-allowed' : 'pointer',
                    opacity: (!passwordFormValid || updatingPassword) ? 0.6 : 1
                  }}
                >
                  {updatingPassword ? modalTranslations.security.updating : (passwordFormValid ? modalTranslations.security.updateBtn : modalTranslations.security.continue)}
                </button>
              </form>
            </div>
          </div>
        )}
        {/* Profile Section - AuthScreens Style */}
        <div style={{
          padding: '32px 16px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          {/* Avatar Area */}
          <div
            onClick={() => setShowEdit(true)}
            style={{
              position: 'relative',
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            {profile.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt="Avatar"
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #FFFFFF',
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
                }}
              />
            ) : (
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(145deg, #F7F6F2, #EEECEA)',
                border: '0.5px solid #E4E2DB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                color: '#9A9890',
                fontWeight: '600'
              }}>
                {(profile.fullname || profile.first_name || 'П').charAt(0).toUpperCase()}
              </div>
            )}
            <div style={{
              position: 'absolute',
              bottom: '0px',
              right: '0px',
              width: '20px',
              height: '20px',
              background: '#FFFFFF',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '0.5px solid #EAEAE4',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
            }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </div>
          </div>

          <div style={{ textAlign: 'left', flex: 1 }}>
            <h1 style={{
              fontSize: '22px',
              fontWeight: '600',
              color: '#111',
              margin: '0 0 4px 0',
              letterSpacing: '-0.5px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
            }}>
              {(`${profile.first_name || ''} ${profile.last_name || ''}`.trim()) || profile.fullname || t('profile.user') || 'Пользователь'}
            </h1>

            <p style={{
              fontSize: '13px',
              color: '#9A9890',
              fontWeight: '400',
              margin: 0,
              letterSpacing: '-0.1px'
            }}>
              {formatPhone(profile.phone)}
            </p>
          </div>
        </div>

        {/* Minimalist Action List - AuthScreens Style */}
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          padding: '0 16px 60px'
        }}>
          <div className="profile-card-auth">
            {/* Account Settings */}
            <button
              onClick={() => setShowEdit(true)}
              className="profile-btn-auth"
              style={{
                width: '100%',
                padding: '16px 20px',
                background: 'transparent',
                border: 'none',
                borderBottom: '0.5px solid #EAEAE4',
                borderRadius: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                cursor: 'pointer',
                transition: 'background 0.15s ease',
                WebkitTapHighlightColor: 'transparent',
                fontFamily: 'inherit'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F7F6F2'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ width: '40px', height: '40px', background: 'linear-gradient(145deg, #F7F6F2, #EEECEA)', borderRadius: '12px', border: '0.5px solid #E4E2DB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#111', flex: 1, textAlign: 'left', letterSpacing: '-0.1px' }}>
                {lang === 'kk' ? 'Профильді өзгерту' : lang === 'ru' ? 'Редактировать профиль' : 'Edit Profile'}
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9A9890" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            {/* Language */}
            <button
              onClick={() => setShowLangSelector(true)}
              className="profile-btn-auth"
              style={{
                width: '100%',
                padding: '16px 20px',
                background: 'transparent',
                border: 'none',
                borderBottom: '0.5px solid #EAEAE4',
                borderRadius: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                cursor: 'pointer',
                transition: 'background 0.15s ease',
                WebkitTapHighlightColor: 'transparent',
                fontFamily: 'inherit'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F7F6F2'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ width: '40px', height: '40px', background: 'linear-gradient(145deg, #F7F6F2, #EEECEA)', borderRadius: '12px', border: '0.5px solid #E4E2DB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              </div>
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#111', flex: 1, textAlign: 'left', letterSpacing: '-0.1px' }}>
                {lang === 'kk' ? 'Тілді таңдау' : lang === 'ru' ? 'Выбор языка' : 'Language'}
              </span>
              <span style={{ fontSize: '13px', fontWeight: '500', color: '#9A9890', marginRight: '4px' }}>
                {lang === 'kk' ? 'Қаз' : lang === 'ru' ? 'Рус' : 'Eng'}
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9A9890" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            {/* Security */}
            <button
              onClick={() => setShowSecurityModal(true)}
              className="profile-btn-auth"
              style={{
                width: '100%',
                padding: '16px 20px',
                background: 'transparent',
                border: 'none',
                borderBottom: '0.5px solid #EAEAE4',
                borderRadius: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                cursor: 'pointer',
                transition: 'background 0.15s ease',
                WebkitTapHighlightColor: 'transparent',
                fontFamily: 'inherit'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F7F6F2'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ width: '40px', height: '40px', background: 'linear-gradient(145deg, #F7F6F2, #EEECEA)', borderRadius: '12px', border: '0.5px solid #E4E2DB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#111', flex: 1, textAlign: 'left', letterSpacing: '-0.1px' }}>
                {lang === 'kk' ? 'Қауіпсіздік' : lang === 'ru' ? 'Безопасность' : 'Security'}
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9A9890" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            {/* Support */}
            <button
              onClick={() => setShowSupportModal(true)}
              className="profile-btn-auth"
              style={{
                width: '100%',
                padding: '16px 20px',
                background: 'transparent',
                border: 'none',
                borderBottom: '0.5px solid #EAEAE4',
                borderRadius: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                cursor: 'pointer',
                transition: 'background 0.15s ease',
                WebkitTapHighlightColor: 'transparent',
                fontFamily: 'inherit'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F7F6F2'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ width: '40px', height: '40px', background: 'linear-gradient(145deg, #F7F6F2, #EEECEA)', borderRadius: '12px', border: '0.5px solid #E4E2DB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#111', flex: 1, textAlign: 'left', letterSpacing: '-0.1px' }}>
                {lang === 'kk' ? 'Қолдау' : lang === 'ru' ? 'Поддержка' : 'Support'}
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9A9890" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            {/* Logout */}
            <button
              onClick={() => setShowLogoutConfirm(true)}
              style={{
                width: '100%',
                padding: '16px 20px',
                background: 'transparent',
                border: 'none',
                borderRadius: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                cursor: 'pointer',
                transition: 'background 0.15s ease',
                WebkitTapHighlightColor: 'transparent',
                fontFamily: 'inherit'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#FDF4F4'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ width: '40px', height: '40px', background: '#FDF4F4', borderRadius: '12px', border: '0.5px solid rgba(220,38,38,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                </svg>
              </div>
              <span style={{ fontSize: '15px', fontWeight: '500', color: '#B91C1C', flex: 1, textAlign: 'left', letterSpacing: '-0.1px' }}>
                {lang === 'kk' ? 'Шығу' : lang === 'ru' ? 'Выйти' : 'Logout'}
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}>
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>

        {/* Language Selector Modal */}
        {showLangSelector && (
          <div
            onClick={() => setShowLangSelector(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
              backdropFilter: 'blur(10px)', zIndex: 3000,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#FFFFFF', borderRadius: '28px', padding: '28px 24px',
                width: '100%', maxWidth: '420px', border: '0.5px solid #EAEAE4',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)'
              }}
            >
              <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', textAlign: 'center', color: '#111', letterSpacing: '-0.5px' }}>
                {modalTranslations.language.title}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { code: 'kk', name: 'Қазақша' },
                  { code: 'ru', name: 'Русский' },
                  { code: 'en', name: 'English' }
                ].map((item) => (
                  <button
                    key={item.code}
                    onClick={() => {
                      setLang(item.code)
                      setShowLangSelector(false)
                    }}
                    style={{
                      padding: '16px',
                      borderRadius: '16px',
                      border: lang === item.code ? '0.5px solid #C4B8F8' : '0.5px solid #EAEAE4',
                      background: lang === item.code ? '#FFFFFF' : '#F7F6F2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      fontFamily: 'inherit'
                    }}
                  >
                    <span style={{
                      fontSize: '15px',
                      fontWeight: lang === item.code ? '600' : '500',
                      color: lang === item.code ? '#111' : '#111',
                      letterSpacing: '-0.1px'
                    }}>{item.name}</span>
                    {lang === item.code && (
                      <div style={{ marginLeft: 'auto', color: '#111' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20,6 9,17 4,12" />
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowLangSelector(false)}
                className="profile-btn-primary"
                style={{
                  width: '100%',
                  marginTop: '16px',
                  border: '1.5px solid #111',
                  background: '#FFFFFF',
                  color: '#111'
                }}
              >
                {modalTranslations.language.close}
              </button>
            </div>
          </div>
        )}

        {/* Support modal - AuthScreens Style */}
        {showSupportModal && (
          <div
            onClick={() => setShowSupportModal(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              zIndex: 2000,
              animation: 'fadeIn 0.2s ease'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#FFFFFF',
                borderRadius: '28px',
                padding: '28px 24px',
                maxWidth: '420px',
                width: '100%',
                border: '0.5px solid #EAEAE4',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                animation: 'scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)'
              }}
            >
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#111',
                  marginBottom: '6px',
                  letterSpacing: '-0.5px'
                }}>
                  {modalTranslations.support.title}
                </h2>
                <p style={{
                  fontSize: '13px',
                  color: '#9A9890',
                  lineHeight: 1.5,
                  margin: 0
                }}>
                  {modalTranslations.support.description}
                </p>
              </div>

              {/* Telegram Button */}
              <button
                onClick={() => {
                  window.open('https://t.me/sssssrkd', '_blank')
                  setShowSupportModal(false)
                }}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  background: '#F7F6F2',
                  border: '0.5px solid #EAEAE4',
                  borderRadius: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  marginBottom: '12px',
                  fontFamily: 'inherit'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#FFFFFF'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#F7F6F2'}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(145deg, #F7F6F2, #EEECEA)',
                  borderRadius: '12px',
                  border: '0.5px solid #E4E2DB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#111">
                    <path d="M12 0C5.374 0 0 5.373 0 12s5.374 12 12 12 12-5.373 12-12S18.626 0 12 0zm5.568 8.16c-.169 1.858-.896 6.728-.896 6.728-.896 6.728-1.268 7.886-1.268 7.886-.16.708-.534.708-.534.708s-1.924-.64-2.746-1.176c-.534-.356-2.746-1.781-3.467-2.315-.356-.267-.712-.801.178-1.424 2.138-1.781 4.632-4.276 6.226-5.751.712-.658.356-1.07-.356-.658-2.49 1.781-6.584 4.454-7.662 5.145 0 0-1.246.801-3.556-.125-2.31-.926-3.2-1.424-3.2-1.424s-1.246-.801.89-1.602c0 0 8.552-3.556 11.476-4.72C17.368 7.182 17.724 8.16 17.568 8.16z" />
                  </svg>
                </div>
                <div style={{ textAlign: 'left', flex: 1 }}>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#111',
                    marginBottom: '2px',
                    letterSpacing: '-0.1px'
                  }}>
                    Telegram
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#9A9890',
                    fontWeight: '400'
                  }}>
                    @sssssrkd
                  </div>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9A9890" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>

              {/* Close Button */}
              <button
                onClick={() => setShowSupportModal(false)}
                className="profile-btn-primary"
                style={{
                  width: '100%',
                  border: '1.5px solid #111',
                  background: '#FFFFFF',
                  color: '#111'
                }}
              >
                {modalTranslations.support.close}
              </button>
            </div>
          </div>
        )}

        {/* Logout confirmation modal - AuthScreens Style */}
        {showLogoutConfirm && (
          <div
            onClick={() => setShowLogoutConfirm(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              zIndex: 1000,
              animation: 'fadeIn 0.2s ease'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#FFFFFF',
                borderRadius: '28px',
                padding: '28px 24px',
                maxWidth: '420px',
                width: '100%',
                border: '0.5px solid #EAEAE4',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                animation: 'scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)'
              }}
            >
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#111',
                  marginBottom: '6px',
                  letterSpacing: '-0.5px'
                }}>
                  {modalTranslations.logout.title}
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#9A9890',
                  lineHeight: '1.5',
                  margin: 0
                }}>
                  {modalTranslations.logout.message}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="profile-btn-primary"
                  style={{
                    border: '1.5px solid #111',
                    background: '#FFFFFF',
                    color: '#111'
                  }}
                >
                  {modalTranslations.logout.cancel}
                </button>
                <button
                  onClick={handleLogout}
                  className="profile-btn-primary"
                  style={{
                    border: '1.5px solid #DC2626',
                    background: '#FFFFFF',
                    color: '#DC2626'
                  }}
                >
                  {modalTranslations.logout.logout}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
